import type { IncomingCheckResult } from "../components/onboarding/IncomingContentCheck";
import { incomingLink, linkInText } from "./sourceLink";

/** Keep incoming creation content separate from the business website used for branding. */
const fields = ["articleUrl", "inputText", "pendingContent", "sourceDocument", "claimToken", "source", "templateId", "preserveInputTextAsCaption", "intent", "claimIntent", "tab", "toolUrl", "instructions"];
/** Tools whose text is the post itself (formatters): it is kept as the caption, never read as a request. */
export function keepsTextAsCaption(params: URLSearchParams): boolean {
  return ["1", "true"].includes(params.get("preserveInputTextAsCaption") || "") ||
    ["linkedin-text-formatter", "instagram-font-generator"].includes(params.get("source") || "");
}
export function creationIntent(search: string): string | null {
  const input = new URLSearchParams(search);
  if (!input.get("sourceDocument")?.trim() && !input.get("templateId")?.trim() && !input.get("articleUrl")?.trim() && !input.get("inputText")?.trim() &&
      !(input.get("pendingContent") && input.get("claimToken"))) return null;
  const output = new URLSearchParams();
  fields.forEach(key => { const value = input.get(key); if (value) output.set(key, value); });
  // Text that is one link with a request around it is read as that link.
  const text = output.get("inputText");
  const found = text && !output.get("articleUrl") && !keepsTextAsCaption(output) ? linkInText(text) : null;
  if (found) {
    output.delete("inputText");
    output.set("articleUrl", found.link);
    output.set("tab", "url");
    if (!output.get("instructions")) output.set("instructions", found.instructions);
  }
  // A request typed around the link travels as instructions, beside the link.
  const article = output.get("articleUrl");
  if (article) {
    const { link, instructions } = incomingLink(article);
    output.set("articleUrl", link);
    if (instructions && !output.get("instructions")) output.set("instructions", instructions);
  }
  return output.toString();
}
const key = (userId: string, workspaceId: string) => `onboarding-content:${userId}:${workspaceId}`;
export function rememberCreationIntent(userId: string, workspaceId: string, search: string) {
  const intent = creationIntent(search);
  if (intent) sessionStorage.setItem(key(userId, workspaceId), intent);
  return intent;
}
export function readCreationIntent(userId: string, workspaceId: string): string | null {
  try { return creationIntent(sessionStorage.getItem(key(userId, workspaceId)) || ""); }
  catch { return null; }
}
export function clearCreationIntent(userId: string, workspaceId: string) {
  try {
    sessionStorage.removeItem(key(userId, workspaceId));
    sessionStorage.removeItem(checkKey(userId, workspaceId));
  } catch { /* Storage may be unavailable. */ }
}

// The source check (and a choice to fix the source later) holds for the whole
// setup, so reloads, Back, and the OAuth round trip don't ask again.
const checkKey = (userId: string, workspaceId: string) => `onboarding-source-check:${userId}:${workspaceId}`;
export function rememberSourceCheck(userId: string, workspaceId: string, intent: string, result: IncomingCheckResult) {
  try { sessionStorage.setItem(checkKey(userId, workspaceId), JSON.stringify({ intent, result })); }
  catch { /* Without storage the source is simply checked again. */ }
}
export function readSourceCheck(userId: string, workspaceId: string, intent: string): IncomingCheckResult | null {
  try {
    const stored = JSON.parse(sessionStorage.getItem(checkKey(userId, workspaceId)) || "null");
    const result = stored?.result;
    if (stored?.intent !== intent || !["readable", "unverified"].includes(result?.status) ||
        !["link", "pdf", "content"].includes(result?.kind) || typeof result?.message !== "string") return null;
    const youtube = result.youtube;
    return {
      status: result.status,
      kind: result.kind,
      message: result.message,
      ...(typeof result.title === "string" ? { title: result.title } : {}),
      ...(typeof result.confirmed === "boolean" ? { confirmed: result.confirmed } : {}),
      ...(typeof youtube?.videoId === "string" && /^https:\/\/i\.ytimg\.com\//.test(youtube?.thumbnailUrl)
        ? { youtube: {
            videoId: youtube.videoId,
            thumbnailUrl: youtube.thumbnailUrl,
            ...(typeof youtube.channel === "string" ? { channel: youtube.channel } : {}),
            ...(typeof youtube.durationSec === "number" ? { durationSec: youtube.durationSec } : {}),
          } }
        : {}),
    };
  } catch { return null; }
}
export function creationDestination(intent: string | null): string {
  if (!intent) return "/generate";
  const params = new URLSearchParams(intent);
  params.set("setup", "ready");
  return `/generate?${params.toString()}`;
}

/** Internal destinations only. Never turn a handoff into an external redirect. */
export function internalReturnPath(value: unknown): string | null {
  if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//") || value.includes("\\")) return null;
  try {
    const url = new URL(value, "https://app.invalid");
    if (url.origin !== "https://app.invalid") return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch { return null; }
}

export function onboardingReturnPath(value: unknown): string | null {
  const path = internalReturnPath(value);
  if (!path) return null;
  const url = new URL(path, "https://app.invalid");
  if (url.pathname === "/generate") {
    const intent = creationIntent(url.search);
    return intent ? `/generate?${intent}` : "/generate";
  }
  if (url.pathname === "/create-post" && url.searchParams.get("scheduledTime")) {
    const params = new URLSearchParams();
    for (const key of ["scheduledTime", "source"]) {
      const value = url.searchParams.get(key);
      if (value) params.set(key, value);
    }
    return `/create-post?${params}`;
  }
  return null;
}

export function pricingDestination(planId: string, returnTo: string): string {
  return `/upgrade?${new URLSearchParams({ planId, backTo: internalReturnPath(returnTo) || "/generate" })}`;
}
